/**
 * Permissions store.
 *
 * Migrated from the previous singleton composable in
 * `src/composables/usePermissions.ts`. Same semantics, now backed by
 * Pinia so it shows up in DevTools and is consistent with the auth /
 * property stores.
 *
 * The composable still exists at the original path as a thin shim — every
 * existing call site (`const { canCreate } = usePermissions()`) keeps
 * working unchanged. The shim merges `storeToRefs` (for the reactive
 * fields) with the actions, so destructuring preserves reactivity.
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getDBJWT } from '@/services/superTokensApiService'
import type { RolePermission } from '@/config/permissions'
import { allPages } from '@/config/permissions'

const EVENTS_API_URL = import.meta.env.VITE_POSTGREST_URL || 'https://events-api.app.kernboards.com'

// Pages that are permission-controlled but not actual routes (modals/features)
const NON_ROUTED_PAGES = ['report', 'documentation', 'event-series', 'maya-assistant', 'system-status', 'mews-finance']

const PAGE_ALIASES: Record<string, string> = {
  'mice-analytics': 'mice-board',
}

const ADMIN_ROLES = ['adg-admin', 'adg-superuser', 'adg-superadmin']

function decodeJWTRoles(token: string): string[] {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(jsonPayload)
    // EventHub-Backend's /api/auth/db-jwt (used for Entra users) puts
    // the user's app-level roles in `app_roles` — the top-level `role`
    // field is the Postgres role used for SET ROLE, not what we gate UI
    // on. Kern-auth-be's older format uses `app.roles` or `roles`.
    if (payload.app_roles && Array.isArray(payload.app_roles)) return payload.app_roles
    if (payload.app?.roles && Array.isArray(payload.app.roles)) return payload.app.roles
    if (payload.roles && Array.isArray(payload.roles)) return payload.roles
    return []
  } catch {
    return []
  }
}

export const usePermissionsStore = defineStore('permissions', () => {
  const userRoles = ref<string[]>([])
  const rolePermissions = ref<RolePermission[]>([])
  const loaded = ref(false)
  const loading = ref(false)
  // True when the last load attempt failed for a reason that says nothing
  // about the user's roles (network, timeout, backend 5xx). Distinct from
  // `loaded` so the dashboard can show "couldn't reach the server — retry"
  // instead of the "No Access" screen, which is only true once we have
  // actually read an empty permission set.
  const loadError = ref(false)
  let inflight: Promise<void> | null = null

  async function loadPermissions(): Promise<void> {
    if (inflight) return inflight
    if (loaded.value) return
    loading.value = true

    inflight = (async () => {
      let success = false
      loadError.value = false
      try {
        // Throws on any failure to mint (transient or definitive — the
        // definitive ones have already redirected to /login by the time it
        // rejects), never resolves null.
        const token = await getDBJWT()

        userRoles.value = decodeJWTRoles(token)
        console.log('JWT roles:', userRoles.value)

        if (userRoles.value.length > 0) {
          const roleFilter = userRoles.value.map(r => `"${r}"`).join(',')
          const url = `${EVENTS_API_URL}/role_permissions?role_name=in.(${roleFilter})&select=*`
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 10_000)
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          })
          clearTimeout(timeoutId)
          if (response.ok) {
            rolePermissions.value = await response.json()
            console.log('Role permissions loaded:', rolePermissions.value.length, 'entries')
            success = true
          } else if (response.status === 401) {
            console.log('Session expired while loading permissions — redirecting to login')
            window.location.href = '/login'
            return
          } else {
            console.warn('Failed to load role_permissions:', response.status)
            rolePermissions.value = []
          }
        } else {
          // No roles is a valid terminal state (e.g., anonymous user) — mark loaded
          success = true
        }
      } catch (error) {
        // A failed load is NOT an empty permission set. Leave `loaded`
        // false so the router guard retries on the next navigation, and
        // flag it so the dashboard can say "couldn't reach the server".
        // Marking this loaded (as it used to) rendered the "No Access"
        // screen after every offline boot.
        console.error('Failed to load permissions:', error)
        rolePermissions.value = []
        loadError.value = true
      } finally {
        if (success) loaded.value = true
        loading.value = false
        inflight = null
      }
    })()
    return inflight
  }

  function resetPermissions(): void {
    userRoles.value = []
    rolePermissions.value = []
    loaded.value = false
    loading.value = false
    loadError.value = false
    inflight = null
  }

  function getPagePermission(pageKey: string): { canRead: boolean; canCreate: boolean; canEdit: boolean; canDelete: boolean } {
    const resolvedKey = PAGE_ALIASES[pageKey] || pageKey
    const matching = rolePermissions.value.filter(rp => rp.page_key === resolvedKey)
    if (matching.length === 0) {
      return { canRead: false, canCreate: false, canEdit: false, canDelete: false }
    }
    return {
      canRead: matching.some(rp => rp.can_read),
      canCreate: matching.some(rp => rp.can_create),
      canEdit: matching.some(rp => rp.can_edit),
      // `can_delete` arrives with V201. Against a database that has not run it
      // the field is absent, and delete falls back to the right that used to
      // cover it — otherwise a frontend deployed first would hide every delete
      // button in the app.
      canDelete: matching.some(rp => (rp.can_delete === undefined ? rp.can_edit : rp.can_delete)),
    }
  }

  function canAccess(pageKey: string): boolean {
    if (!loaded.value) return false
    return getPagePermission(pageKey).canRead
  }

  function canCreate(pageKey: string): boolean {
    if (!loaded.value) return false
    return getPagePermission(pageKey).canCreate
  }

  function canEdit(pageKey: string): boolean {
    if (!loaded.value) return false
    return getPagePermission(pageKey).canEdit
  }

  /** Separate from canEdit since V201: erasing is not correcting. */
  function canDelete(pageKey: string): boolean {
    if (!loaded.value) return false
    return getPagePermission(pageKey).canDelete
  }

  const isAdmin = computed(() => userRoles.value.some(r => ADMIN_ROLES.includes(r)))

  const hasAnyAccess = computed(() => {
    if (!loaded.value) return true
    return rolePermissions.value.some(rp => rp.can_read && !NON_ROUTED_PAGES.includes(rp.page_key))
  })

  function firstAccessiblePage(): string | null {
    for (const page of allPages) {
      if (NON_ROUTED_PAGES.includes(page.pageKey)) continue
      // Sub-permission keys (colon-namespaced — e.g. `stream:mice`,
      // `raw:events`) gate features inside a parent route; they are
      // not themselves routes, so we never want to redirect a "first
      // accessible page" lookup at them.
      if (page.pageKey.includes(':')) continue
      if (getPagePermission(page.pageKey).canRead) {
        return page.pageKey
      }
    }
    return null
  }

  return {
    userRoles,
    rolePermissions,
    loaded,
    loading,
    loadError,
    isAdmin,
    hasAnyAccess,
    loadPermissions,
    resetPermissions,
    canAccess,
    canCreate,
    canEdit,
    canDelete,
    firstAccessiblePage,
  }
})
